"use client";

import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { useUnreadCount } from "@/lib/use-unread";
import { getMe, isStaff } from "@/lib/auth-store";
import { Wordmark } from "./Wordmark";
import { isActive, ownProfilePathOf } from "./nav";
import { useNavItems } from "@/lib/use-public-settings";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";

export interface NavRailProps {
  actions?: React.ReactNode;
}

/**
 * Slim desktop left rail: brand at top, vertical primary nav, optional
 * actions pinned to the bottom. Fixed width; shown from md up only.
 */
export function NavRail({ actions }: NavRailProps) {
  const navItems = useNavItems();
  const pathname = usePathname() ?? "";
  const unread = useUnreadCount();
  const t = useTranslations("nav");
  const [staff, setStaff] = useState(false);
  const [ownProfilePath, setOwnProfilePath] = useState<string | null>(null);

  useEffect(() => {
    // Role and identity live in client-only storage, resolved after mount.
    const me = getMe();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStaff(isStaff(me));
    setOwnProfilePath(ownProfilePathOf(me?.username));
  }, []);

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-40 hidden w-64 flex-col md:flex",
        "border-r border-white/8 bg-ink/60 backdrop-blur-xl",
      )}
    >
      <div className="px-5 pt-7 pb-6">
        <Wordmark size="md" href="/deck" />
      </div>

      <nav aria-label={t("primary")} className="flex-1 px-3">
        <ul className="flex flex-col gap-1">
          {navItems.map(({ href, labelKey, Icon }) => {
            const active = isActive(pathname, href, ownProfilePath);
            return (
              <li key={href}>
                <Link
                  href={href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "group relative flex items-center gap-3.5 rounded-xl px-3.5 py-2.5",
                    "text-md font-medium transition-colors",
                    active
                      ? "bg-flamingo/12 text-white"
                      : "text-white/55 hover:bg-white/[0.04] hover:text-white",
                  )}
                >
                  {active && (
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-flamingo shadow-glow-sm"
                    />
                  )}
                  <Icon
                    className={cn(
                      "size-[1.35rem]",
                      active ? "text-flamingo" : "text-current",
                    )}
                  />
                  {t(labelKey)}
                  {href === "/matches" && unread > 0 && (
                    <span className="ml-auto flex min-w-5 items-center justify-center rounded-full bg-flamingo px-1.5 text-xs font-bold text-white">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
          {staff && (
            <li>
              <Link
                href="/admin"
                aria-current={isActive(pathname, "/admin") ? "page" : undefined}
                className={cn(
                  "group relative flex items-center gap-3.5 rounded-xl px-3.5 py-2.5 text-md font-medium transition-colors",
                  isActive(pathname, "/admin")
                    ? "bg-flamingo/12 text-white"
                    : "text-white/55 hover:bg-white/[0.04] hover:text-white",
                )}
              >
                <svg viewBox="0 0 24 24" className="size-[1.35rem]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l7 3v5c0 4.4-3 8.3-7 9.5-4-1.2-7-5.1-7-9.5V6l7-3Z" />
                </svg>
                {t("admin")}
              </Link>
            </li>
          )}
        </ul>
      </nav>

      <div className="mt-auto">
        <div className="border-t border-white/8 px-4 py-3">
          <LocaleSwitcher />
        </div>
        {actions && <div className="border-t border-white/8 px-4 py-4">{actions}</div>}
      </div>
    </aside>
  );
}
